import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type PatientSummaryCtaShownEventProps,
  type PatientSummaryEmailSendAttemptedEventProps,
  type PatientSummaryEmailSentEventProps,
} from '@/models/analytics/features/patient';
import { trackSelfDescribingEvent } from '@snowplow/browser-tracker';
import {
  trackClinicalCodeClickableSpec,
  trackClinicalCodeCopiedSpec,
  trackPatientContextToggleOffSpec,
  trackPatientContextToggleOnSpec,
  trackPatientCreatedSpec,
  trackPatientDeleteBlockedSpec,
  trackPatientDeleteClickedSpec,
  trackPatientDirectoryEnableClickedSpec,
  trackPatientDocumentLinkedSpec,
  trackPatientDocumentUnlinkedSpec,
  trackPatientDocumentUploadedSpec,
  trackPatientExtractionModalOpenedSpec,
  trackPatientExtractionReviewedSpec,
  trackPatientFieldEditedSpec,
  trackPatientLinkedSpec,
  trackPatientListViewedSpec,
  trackPatientProfileOpenedSpec,
  trackPatientProfileTabViewedSpec,
  trackPatientRecapPopoverOpenedSpec,
  trackPatientSearchedSpec,
  trackPatientSelectedSpec,
  trackPatientSessionExpandedSpec,
  trackPatientUnlinkedSpec,
  trackPatientUpdatedSpec,
  trackPatientViewedSpec,
  type ClinicalCodeClickable,
  type ClinicalCodeCopied,
  type PatientContextToggleOff,
  type PatientContextToggleOn,
  type PatientCreated,
  type PatientDeleteBlocked,
  type PatientDeleteClicked,
  type PatientDirectoryEnableClicked,
  type PatientDocumentLinked,
  type PatientDocumentUnlinked,
  type PatientDocumentUploaded,
  type PatientFieldEdited,
  type PatientLinked,
  type PatientListViewed,
  type PatientProfileOpened,
  type PatientProfileTabViewed,
  type PatientRecapPopoverOpened,
  type PatientSearched,
  type PatientSelected,
  type PatientSessionExpanded,
  type PatientUnlinked,
} from '@snowtype/snowplow';
import type { SnowplowEventMap } from '../trackSnowplowEvent';

export const patientEvents: SnowplowEventMap = {
  [TrackingEventName.PatientViewed]: (props) =>
    trackPatientViewedSpec(props as Parameters<typeof trackPatientViewedSpec>[0]),
  [TrackingEventName.PatientCreated]: (props) =>
    trackPatientCreatedSpec(props as PatientCreated),
  [TrackingEventName.PatientUpdated]: (props) =>
    trackPatientUpdatedSpec(props as Parameters<typeof trackPatientUpdatedSpec>[0]),
  [TrackingEventName.PatientLinked]: (props) =>
    trackPatientLinkedSpec(props as PatientLinked),
  [TrackingEventName.PatientUnlinked]: (props) =>
    trackPatientUnlinkedSpec(props as PatientUnlinked),
  [TrackingEventName.PatientSelected]: (props) =>
    trackPatientSelectedSpec(props as PatientSelected),
  [TrackingEventName.PatientSearched]: (props) =>
    trackPatientSearchedSpec(props as PatientSearched),
  [TrackingEventName.PatientContextToggleOn]: (props) =>
    trackPatientContextToggleOnSpec(props as PatientContextToggleOn),
  [TrackingEventName.PatientContextToggleOff]: (props) =>
    trackPatientContextToggleOffSpec(props as PatientContextToggleOff),
  [TrackingEventName.ExtractionModalOpened]: (props) =>
    trackPatientExtractionModalOpenedSpec(
      props as Parameters<typeof trackPatientExtractionModalOpenedSpec>[0]
    ),
  [TrackingEventName.ExtractionReviewed]: (props) =>
    trackPatientExtractionReviewedSpec(
      props as Parameters<typeof trackPatientExtractionReviewedSpec>[0]
    ),
  [TrackingEventName.ClinicalCodeClickable]: (props) =>
    trackClinicalCodeClickableSpec(props as ClinicalCodeClickable),
  [TrackingEventName.ClinicalCodeCopied]: (props) =>
    trackClinicalCodeCopiedSpec(props as ClinicalCodeCopied),
  [TrackingEventName.PatientListViewed]: (props) =>
    trackPatientListViewedSpec(props as PatientListViewed),
  [TrackingEventName.PatientProfileOpened]: (props) =>
    trackPatientProfileOpenedSpec(props as PatientProfileOpened),
  [TrackingEventName.PatientProfileTabViewed]: (props) =>
    trackPatientProfileTabViewedSpec(props as PatientProfileTabViewed),
  [TrackingEventName.PatientSessionExpanded]: (props) =>
    trackPatientSessionExpandedSpec(props as PatientSessionExpanded),
  [TrackingEventName.PatientFieldEdited]: (props) =>
    trackPatientFieldEditedSpec(props as PatientFieldEdited),
  [TrackingEventName.PatientDocumentUploaded]: (props) =>
    trackPatientDocumentUploadedSpec(props as PatientDocumentUploaded),
  [TrackingEventName.PatientDocumentLinked]: (props) =>
    trackPatientDocumentLinkedSpec(props as PatientDocumentLinked),
  [TrackingEventName.PatientDocumentUnlinked]: (props) =>
    trackPatientDocumentUnlinkedSpec(props as PatientDocumentUnlinked),
  [TrackingEventName.PatientRecapPopoverOpened]: (props) =>
    trackPatientRecapPopoverOpenedSpec(props as PatientRecapPopoverOpened),
  [TrackingEventName.PatientDirectoryEnableClicked]: (props) =>
    trackPatientDirectoryEnableClickedSpec(props as PatientDirectoryEnableClicked),
  [TrackingEventName.PatientDeleteClicked]: (props) =>
    trackPatientDeleteClickedSpec(props as PatientDeleteClicked),
  [TrackingEventName.PatientDeleteBlocked]: (props) =>
    trackPatientDeleteBlockedSpec(props as PatientDeleteBlocked),

  // patient_summary_email_* — hand-rolled self-describing calls. The Iglu
  // schemas are published to prod at 1-0-1 (adds `variant`), but the
  // checked-in Snowtype output can't be regenerated here (the console API
  // credentials live in 1Password / CI). TODO(snowtype): once
  // snowtype/snowplow.ts is regenerated with console creds, swap these to the
  // generated trackPatientSummaryEmail*Spec functions. The URIs below must
  // match data-structures/com.heidi/patient_summary_email_*.yaml exactly.
  [TrackingEventName.PatientSummaryEmailSendAttempted]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: 'iglu:com.heidi/patient_summary_email_send_attempted/jsonschema/1-0-1',
        data: props as PatientSummaryEmailSendAttemptedEventProps,
      },
    }),
  [TrackingEventName.PatientSummaryEmailSent]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: 'iglu:com.heidi/patient_summary_email_sent/jsonschema/1-0-1',
        data: props as PatientSummaryEmailSentEventProps,
      },
    }),
  // patient_summary_cta_shown — hand-rolled self-describing call, same reason
  // as the email events above (Snowtype output can't be regenerated without the
  // console API credentials). The URI must match
  // data-structures/com.heidi/patient_summary_cta_shown.yaml exactly.
  [TrackingEventName.PatientSummaryCtaShown]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: 'iglu:com.heidi/patient_summary_cta_shown/jsonschema/1-0-0',
        data: props as PatientSummaryCtaShownEventProps,
      },
    }),
};
